import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import {
  useAuthStore,
  useListenForDataUpdates,
  useListenForUserUpdates,
  useListenToSubscription,
  useWorkspaceSettings,
} from '@easy-expense/data-firestore-client';
import { WebEnv } from '@easy-expense/env-mobile';
import { theme } from '@easy-expense/ui-theme';
import {
  omitNil,
  TEAM_CHECKOUT_PATH,
  TEAM_CHECKOUT_SUCCESS_PATH,
} from '@easy-expense/utils-shared';
import { CurrencyType } from '@phensley/cldr';
import React from 'react';
import ReactPixel from 'react-facebook-pixel';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';

import { Debug } from './components/Debug.component';
import { AppLayout } from './components/Layout.component';
import { PlanGateProvider } from './components/Paywall/PlanGateProvider.component';
import { RequireAuth } from './components/RequireAuth.component';
import { RequireManager } from './components/RequireManager.component';
import { RequirePlan } from './components/RequirePlan.component';
import './App.css';
import { useFreshchat } from './hooks/freshchat';
import { useCheckIssuingPermissionsWeb } from './hooks/issuing/useCheckIssuingPermissionsWeb';
import { useAdsClickID } from './hooks/useAdsClickID';
import { MeteredPaywallActions } from './hooks/useHandleMeteredAction';
import { useInitAuth } from './hooks/useInitAuth';
import { INDIVIDUAL_CHECKOUT_PATH } from './lib/Billing/individualCheckout';
import { BankLinkingOnboarding } from './pages/BankLinkingOnboarding/BankLinkingOnboarding';
import { ReceiptScanningOnboarding } from './pages/ReceiptScanningOnboarding/ReceiptScannerOnboarding';
import { AppDownloadPage } from './pages/appDownload.page';
import { ApprovalsPage } from './pages/approvals.page';
import { ExpenseEditPage } from './pages/expenseEdit.page';
import { Export } from './pages/export.page';
import { IndividualCheckoutPage } from './pages/individualCheckout.page';
import { InviteAccept } from './pages/invite.page';
import { InviteAcceptSuccess } from './pages/inviteSuccess.page';
import { Invoice } from './pages/invoice.page';
import { Issuing } from './pages/issuing.page';
import { Login } from './pages/login.page';
import { ComeBackPage } from './pages/onboarding/comeBack.page';
import { DripSetupPage } from './pages/onboarding/dripSetup.page';
import { GetStartedPage } from './pages/onboarding/getStarted.page';
import { HowDoYouWantToUsePage } from './pages/onboarding/howDoYouWantToUse.page';
import { SignupTeamsPage } from './pages/onboarding/signupTeams.page';
import { TeamSetupPage } from './pages/onboarding/teamSetup.page';
import { TeamsInvitePage } from './pages/onboarding/teamsInvite.page';
import { PartnerLandingPage } from './pages/partner.page';
import { Pay } from './pages/pay.page';
import { Reconcile } from './pages/reconcile.page';
import { ReportApprovalsPage } from './pages/report-approvals.page';
import { ReportDetailPage } from './pages/reportDetail.page';
import { Reports } from './pages/reports.page';
import { ReviewTransactionEditPage } from './pages/reviewTransactionEdit.page';
import { SettingsPage } from './pages/settings.page';
import { SignUp } from './pages/signup.page';
import { StatementDetailPage } from './pages/statementDetail.page';
import { TeamCheckoutPage } from './pages/teamCheckout.page';
import { TeamCheckoutSuccessPage } from './pages/teamCheckoutSuccess.page';
import { Transactions } from './pages/transactions.page';
import { Trips } from './pages/trips.page';
import { Workspace } from './pages/workspace.page';

import { getSetProfileFn } from '@easy-expense/auth-client';
import { useIntlStore } from '@easy-expense/intl-client';

function useInitInternationalization() {
  const { data } = useWorkspaceSettings();
  const { setCurrency, currency } = useIntlStore();

  React.useEffect(() => {
    const settings = data?.settings;
    if (settings) {
      if (settings.currencyCode !== currency) {
        setCurrency(data?.settings.currencyCode as CurrencyType);
      }
    }
  }, [data]);
}

Bugsnag.start({
  apiKey: WebEnv.bugsnag.apiKey,
  plugins: [new BugsnagPluginReact() as any],
});

const ErrorBoundary = Bugsnag.getPlugin('react')?.createErrorBoundary(React);

const ErrorView = () => (
  <div>
    <p>
      Oh no, something has gone wrong. Please contact support@easy-expense.com and we will sort this
      out :)
    </p>
  </div>
);

const OTHER_META_PIXEL_ID = '1379037826320805';

export function App() {
  useListenForUserUpdates();

  const currentUser = useAuthStore.use.currentUser();

  React.useEffect(() => {
    if (currentUser?.uid) {
      // @ts-ignore
      ReactPixel.init(OTHER_META_PIXEL_ID, { external_id: currentUser.uid });
    }
  }, [currentUser?.uid]);

  useListenForDataUpdates();
  useInitInternationalization();
  useInitAuth();
  // Keeps useSubscriptionStore's cross-platform ownership flags (isProPlanSubscriptionOwner etc.)
  // populated on web too, the same way apps/ee-mobile's useAppLoading already does -- previously
  // only mobile called this, so web had no reliable way to tell "am I the one whose subscription
  // covers this workspace" apart from the less precise per-component checks it wrote instead
  // (EZ-4952).
  useListenToSubscription();

  const enabled = useCheckIssuingPermissionsWeb();
  useFreshchat();
  React.useEffect(() => {
    if (currentUser) {
      getSetProfileFn()(omitNil({ email: currentUser.email })).catch(() => {});
    }
  }, [currentUser?.email]);

  return (
    // @ts-expect-error
    <ErrorBoundary FallbackComponent={ErrorView}>
      <ThemeProvider theme={theme}>
        <BrowserRouter>
          <Debug />
          <PlanGateProvider>
            <RoutesWrapper>
              <Route path="/" element={<AppLayout />}>
                <Route
                  index
                  element={
                    <RequireAuth>
                      <Navigate to="/transactions" />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <RequireAuth>
                      <Reports />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/transactions"
                  element={
                    <RequireAuth>
                      <Transactions />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/export"
                  element={
                    <RequireAuth>
                      <Export />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/drives"
                  element={
                    <RequireAuth>
                      <Trips />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/workspace"
                  element={
                    <RequireAuth>
                      <RequireManager>
                        <Workspace />
                      </RequireManager>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/reconcile"
                  element={
                    <RequireAuth>
                      <RequirePlan action={MeteredPaywallActions.RECONCILE_STATEMENTS}>
                        <Reconcile />
                      </RequirePlan>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <RequireAuth>
                      <SettingsPage />
                    </RequireAuth>
                  }
                />
                {enabled ? (
                  <Route
                    path="issuing"
                    element={
                      <RequireAuth>
                        <Issuing />
                      </RequireAuth>
                    }
                  >
                    <Route
                      path=":entity"
                      element={
                        <RequireAuth>
                          <Issuing />
                        </RequireAuth>
                      }
                    />
                  </Route>
                ) : null}
              </Route>
              <Route
                path="/approvals"
                element={
                  <RequireAuth>
                    <RequireManager>
                      <ApprovalsPage />
                    </RequireManager>
                  </RequireAuth>
                }
              />
              <Route
                path="/report-approvals"
                element={
                  <RequireAuth>
                    <RequireManager>
                      <ReportApprovalsPage />
                    </RequireManager>
                  </RequireAuth>
                }
              />
              <Route
                path="/expense"
                element={
                  <RequireAuth>
                    <ExpenseEditPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/review"
                element={
                  <RequireAuth>
                    <ReviewTransactionEditPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/review/:transactionType"
                element={
                  <RequireAuth>
                    <ReviewTransactionEditPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/expense/:expenseKey"
                element={
                  <RequireAuth>
                    <ExpenseEditPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/report"
                element={
                  <RequireAuth>
                    <ReportDetailPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/report/:reportKey"
                element={
                  <RequireAuth>
                    <ReportDetailPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/statement/:statementKey"
                element={
                  <RequireAuth>
                    <RequirePlan action={MeteredPaywallActions.RECONCILE_STATEMENTS}>
                      <StatementDetailPage />
                    </RequirePlan>
                  </RequireAuth>
                }
              />

              <Route path="/teams/onboarding/:planCode" element={<SignupTeamsPage />} />
              <Route
                path="/teams/pay/:planCode"
                element={
                  <RequireAuth>
                    <TeamSetupPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/teams/invite"
                element={
                  <RequireAuth>
                    <TeamsInvitePage />
                  </RequireAuth>
                }
              />
              <Route
                path="/workspace-type"
                element={
                  <RequireAuth>
                    <HowDoYouWantToUsePage />
                  </RequireAuth>
                }
              />
              <Route path="/pay" element={<Pay />} />
              <Route path={TEAM_CHECKOUT_PATH} element={<TeamCheckoutPage />} />
              <Route path={INDIVIDUAL_CHECKOUT_PATH} element={<IndividualCheckoutPage />} />
              <Route path={TEAM_CHECKOUT_SUCCESS_PATH} element={<TeamCheckoutSuccessPage />} />
              <Route path="/partner" element={<PartnerLandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/onboarding" element={<SignUp />} />
              <Route path="/welcome" element={<ReceiptScanningOnboarding />} />
              <Route path="/deduction-finder" element={<BankLinkingOnboarding />} />
              <Route path="/discount" element={<DripSetupPage />} />
              <Route path="/come-back" element={<ComeBackPage />} />
              <Route path="/invite" element={<InviteAccept />} />
              <Route path="/accepted" element={<InviteAcceptSuccess />} />
              <Route path="/onboarding/get-started" element={<GetStartedPage />} />
              <Route path="/app-download" element={<AppDownloadPage />} />
              <Route path="/invoice/:paymentID/:accountID" element={<Invoice />} />
              <Route path="*" element={<NotFound />} />
              <Route path="/404" element={<NotFound />} />
            </RoutesWrapper>
          </PlanGateProvider>
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

const NotFound: React.FC = () => {
  return <Navigate to="/login" replace />;
};

const RoutesWrapper: React.FC<React.PropsWithChildren> = (props) => {
  const { children } = props;
  const location = useLocation();
  useAdsClickID();
  const prevRoute = React.useRef<string>('');

  React.useEffect(() => {
    if (location.pathname !== prevRoute.current) {
      if (__DEV__) {
        console.log('Route Change:', location.pathname);
      }

      prevRoute.current = location.pathname;
    }
  }, [location]);

  return <Routes>{children}</Routes>;
};
